import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

import type { GeneratedGame } from '../components/GeneratedGamesSection'
import type { DivisionItem } from '../hooks/useSchedulerDivisions'
import type { DivisionRow, TeamRow } from '../components/ScheduleReviewTable'
import type {
  SchedulerRequestGame,
  SchedulerViolation,
} from '../hooks/useSchedulerGeneration'
import { classifyViolation, dedupeViolations } from './violations'

const SCORE_THRESHOLDS = { EXCELLENT: 96, GOOD: 90 } as const

/** Conflict/advisory counts that cap the score word (shared policy). */
export interface ScoreLabelCounts {
  conflicts?: number
  advisories?: number
}

/**
 * Get the score label from a satisfaction score, made honest about the badges
 * shown beside it:
 *   - any open conflict        → caps at "NEEDS IMPROVEMENT" (regardless of %)
 *   - any open advisory        → caps at "GOOD" (never "EXCELLENT")
 *   - clean (0/0)              → the raw numeric mapping (≥96 EXCELLENT, ≥90 GOOD)
 * Info never caps, so it is intentionally not part of `opts`. The fill-completeness
 * verdict does NOT cap either (2026-06-30: the label reports STRICTLY on conflicts
 * and advisories; fill maximality is surfaced only as an informational note). A
 * zero-violation fill schedule therefore reads EXCELLENT even if it left fillable
 * slots empty. `opts` is optional so existing callers keep the pure numeric mapping.
 */
export function getScoreLabel(
  score: number | null,
  opts?: ScoreLabelCounts
): string {
  if (score === null) {
    return '--'
  }
  const conflicts = opts?.conflicts ?? 0
  const advisories = opts?.advisories ?? 0
  if (conflicts > 0) {
    return 'NEEDS IMPROVEMENT'
  }
  if (advisories > 0) {
    return score >= SCORE_THRESHOLDS.GOOD ? 'GOOD' : 'NEEDS IMPROVEMENT'
  }
  if (score >= SCORE_THRESHOLDS.EXCELLENT) {
    return 'EXCELLENT'
  }
  if (score >= SCORE_THRESHOLDS.GOOD) {
    return 'GOOD'
  }
  return 'NEEDS IMPROVEMENT'
}

/**
 * Attribute conflicts/advisories to divisions for the per-division score cap.
 * Team-scoped violations attribute to their teams' home divisions FIRST — the
 * solver's `affected_game_numbers` can be over-broad (span other divisions), so
 * a per-team advisory must only cap its own team's division, never an unrelated
 * one that merely shares the week. Game numbers are a fallback, used only when a
 * violation carries no resolvable team scope (e.g. venue/global constraints).
 * Prevented/info are excluded by the shared policy.
 */
function countViolationsByDivision(
  violations: SchedulerViolation[],
  gameDivisionByNumber: Map<number, string>,
  teamDivisionByTeamUid: Map<string, string>
): Map<string, ScoreLabelCounts> {
  const byDivision = new Map<
    string,
    { conflicts: number; advisories: number }
  >()
  for (const v of dedupeViolations(violations)) {
    const cls = classifyViolation(v.severity, v.is_prevented)
    if (cls !== 'conflict' && cls !== 'advisory') {
      continue
    }
    const divs = new Set<string>()
    // Team scope is authoritative when present (context.team_uid + affected
    // teams) — a per-team violation only caps its own team's division.
    const ctx = v.context ?? {}
    const ctxTeam = typeof ctx.team_uid === 'string' ? ctx.team_uid : null
    const teamUids = ctxTeam
      ? [ctxTeam, ...(v.affected_team_uids ?? [])]
      : v.affected_team_uids ?? []
    for (const t of teamUids) {
      const d = teamDivisionByTeamUid.get(t)
      if (d) {
        divs.add(d)
      }
    }
    // Fall back to game-number divisions only when there's no team scope — the
    // solver's affected_game_numbers can be over-broad (span other divisions).
    if (divs.size === 0) {
      for (const g of v.affected_game_numbers ?? []) {
        const d = gameDivisionByNumber.get(g)
        if (d) {
          divs.add(d)
        }
      }
    }
    for (const d of divs) {
      const entry = byDivision.get(d) ?? { conflicts: 0, advisories: 0 }
      if (cls === 'conflict') {
        entry.conflicts++
      } else {
        entry.advisories++
      }
      byDivision.set(d, entry)
    }
  }
  return byDivision
}

/**
 * Transform API games into GeneratedGame[] for GeneratedGamesSection
 */
export function transformGamesToDisplay(
  games: SchedulerRequestGame[],
  divisionItems: DivisionItem[]
): GeneratedGame[] {
  return games.map((game) => {
    const divItem = divisionItems.find((d) => d.id === game.division_uid)
    return {
      id: game.id,
      gameNumber: game.game_number,
      globalGameNumber: game.global_game_number ?? null,
      date: dayjs
        .utc(game.starts_at)
        .tz(game.venue?.timezone || 'UTC')
        .format('ddd, MMM D, YYYY'),
      time: dayjs
        .utc(game.starts_at)
        .tz(game.venue?.timezone || 'UTC')
        .format('h:mm A'),
      location: game.venue?.name ?? 'Unknown Venue',
      locationId: game.venue?.id,
      surface: game.facility?.name ?? 'Unknown Surface',
      surfaceId: game.facility?.id,
      divisionId: game.division_uid,
      division: divItem?.name ?? game.division_uid,
      homeTeam: game.home_team?.name ?? 'TBD',
      homeTeamId: game.home_team?.id,
      homeTeamImportName: game.home_team?.name_full ?? undefined,
      awayTeam: game.visiting_team?.name ?? 'TBD',
      awayTeamId: game.visiting_team?.id,
      awayTeamImportName: game.visiting_team?.name_full ?? undefined,
      hasError: false,
      hasWarning: game.approval_status === 'rejected',
      startsAt: game.starts_at,
      endsAt: game.ends_at,
      timezone: game.venue?.timezone || undefined,
    }
  })
}

/**
 * Transform API games into DivisionRow[] for ScheduleReviewTable
 */
export function transformGamesToDivisionRows(
  games: SchedulerRequestGame[],
  divisionItems: DivisionItem[],
  satisfactionScore: number | null,
  violations: SchedulerViolation[] = [],
  gameDivisionByNumber: Map<number, string> = new Map(),
  teamDivisionByTeamUid: Map<string, string> = new Map()
): DivisionRow[] {
  // Per-division conflict/advisory counts, for the honest per-division score cap.
  const violationsByDivision = countViolationsByDivision(
    violations,
    gameDivisionByNumber,
    teamDivisionByTeamUid
  )

  // Group games by division_uid
  const byDivision = new Map<string, SchedulerRequestGame[]>()
  for (const game of games) {
    const key = game.division_uid
    if (!byDivision.has(key)) {
      byDivision.set(key, [])
    }
    byDivision.get(key)!.push(game)
  }

  const divisionRows: DivisionRow[] = []
  for (const [divUid, divGames] of byDivision) {
    // Find the division name from our divisions data
    const divItem = divisionItems.find((d) => d.id === divUid)
    const divName = divItem?.name ?? divUid

    // Count games per team
    const teamGameCounts = new Map<
      string,
      { home: number; visitor: number; name: string; startMinutes: number[] }
    >()
    for (const g of divGames) {
      if (g.home_team) {
        const existing = teamGameCounts.get(g.home_team.id) ?? {
          home: 0,
          visitor: 0,
          name: g.home_team.name,
          startMinutes: [],
        }
        existing.home++
        existing.name = g.home_team.name
        if (g.starts_at) {
          const d = dayjs.utc(g.starts_at).tz(g.venue?.timezone || 'UTC')
          existing.startMinutes.push(d.hour() * 60 + d.minute())
        }
        teamGameCounts.set(g.home_team.id, existing)
      }
      if (g.visiting_team) {
        const existing = teamGameCounts.get(g.visiting_team.id) ?? {
          home: 0,
          visitor: 0,
          name: g.visiting_team.name,
          startMinutes: [],
        }
        existing.visitor++
        existing.name = g.visiting_team.name
        if (g.starts_at) {
          const d = dayjs.utc(g.starts_at).tz(g.venue?.timezone || 'UTC')
          existing.startMinutes.push(d.hour() * 60 + d.minute())
        }
        teamGameCounts.set(g.visiting_team.id, existing)
      }
    }

    const formatMinutes = (min: number) =>
      dayjs
        .utc()
        .hour(Math.floor(min / 60))
        .minute(min % 60)
        .format('h:mm A')

    const teamRows: TeamRow[] = []
    for (const [teamId, counts] of teamGameCounts) {
      let avgStartTime = '--'
      let lateStartTime = '--'
      if (counts.startMinutes.length > 0) {
        const sum = counts.startMinutes.reduce((a, b) => a + b, 0)
        avgStartTime = formatMinutes(
          Math.round(sum / counts.startMinutes.length)
        )
        lateStartTime = formatMinutes(Math.max(...counts.startMinutes))
      }
      teamRows.push({
        id: teamId,
        name: counts.name,
        games: counts.home + counts.visitor,
        home: counts.home,
        visitor: counts.visitor,
        avgStartTime,
        lateStartTime,
        exceptions: 0,
      })
    }

    // Mean games per team, kept EXACT here (83 games across 6 teams = 166 team
    // appearances = 27.666…). Rounding happens at render — see
    // `formatGamesPerTeam` — so the flight-level total can still weight each
    // division by its team count instead of averaging already-rounded averages.
    const totalTeams = teamRows.length
    const teamGamesSum = teamRows.reduce((sum, t) => sum + t.games, 0)
    const gamesPerTeam = totalTeams > 0 ? teamGamesSum / totalTeams : 0

    // Compute average start time from game starts_at
    let avgStartTimeStr = '--'
    if (divGames.length > 0) {
      let totalMinutes = 0
      let validCount = 0
      for (const g of divGames) {
        if (g.starts_at) {
          const d = dayjs.utc(g.starts_at).tz(g.venue?.timezone || 'UTC')
          totalMinutes += d.hour() * 60 + d.minute()
          validCount++
        }
      }
      if (validCount > 0) {
        const avgMin = Math.round(totalMinutes / validCount)
        const hours = Math.floor(avgMin / 60)
        const minutes = avgMin % 60
        avgStartTimeStr = dayjs
          .utc()
          .hour(hours)
          .minute(minutes)
          .format('h:mm A')
      }
    }

    // Compute division status from games
    let divStatus: 'pending' | 'draft' | 'approved' | 'applied' = 'pending'
    if (divGames.length > 0) {
      const allApplied = divGames.every((g) => g.is_applied === true)
      if (allApplied) {
        divStatus = 'applied'
      } else {
        const allApproved = divGames.every(
          (g) => g.approval_status === 'approved'
        )
        divStatus = allApproved ? 'approved' : 'draft'
      }
    }

    divisionRows.push({
      id: divUid,
      name: divName,
      games: divGames.length,
      gamesPerTeam,
      avgStartTime: avgStartTimeStr,
      players: divItem?.playersCount ?? 0,
      teams: totalTeams,
      divisionScore: getScoreLabel(
        satisfactionScore,
        violationsByDivision.get(divUid)
      ),
      status: divStatus,
      teamRows,
    })
  }

  return divisionRows
}

/**
 * Games per team, rounded to a whole number.
 *
 * A division rarely divides evenly — 83 games across 6 teams is 166 team
 * appearances, a mean of 27.666… — and a games-per-team column has no business
 * showing "27.7". Round it. (Individual teams' exact counts are one click away
 * in the expanded team rows, which are always integers.)
 */
export function formatGamesPerTeam(gamesPerTeam: number): string {
  if (!Number.isFinite(gamesPerTeam) || gamesPerTeam <= 0) {
    return '--'
  }
  return `${Math.round(gamesPerTeam)}`
}
